/**
 * Jurisdiction group derivation for the entity selector dropdown.
 *
 * Entity membership is derived from the fetched entities list at runtime
 * (single source of truth: ENTITY_DEFS -> /api/entities -> useEntityStore).
 * Display metadata (flag, label, dot color) is static frontend config.
 *
 * Adding an entity to ENTITY_DEFS surfaces it in the dropdown automatically.
 */

export const GROUP_DISPLAY_META = {
  France:      { label: 'France',         flag: '\uD83C\uDDEB\uD83C\uDDF7', dotColor: 'bg-blue-500',    groupKey: 'group:france' },
  UK:          { label: 'United Kingdom', flag: '\uD83C\uDDEC\uD83C\uDDE7', dotColor: 'bg-emerald-500', groupKey: 'group:uk' },
  'UK/France': { label: 'Personal',      flag: '\uD83D\uDC64',             dotColor: 'bg-orange-500',   groupKey: 'group:personal' },
};

export const GROUP_ORDER = ['France', 'UK', 'UK/France'];

// Map entity country to dot color (used for selected-entity indicator)
export const countryDotColor = Object.fromEntries(
  Object.entries(GROUP_DISPLAY_META).map(([country, m]) => [country, m.dotColor])
);

/**
 * Derive jurisdiction groups from a fetched entities array.
 *
 * Known countries (GROUP_ORDER) get their display metadata from GROUP_DISPLAY_META.
 * Any entity with a null, empty, or unrecognised country lands in a fallback
 * "Other" group so it is never silently dropped from navigation.
 *
 * @param {Array<{id: string, country: string}>} entities
 * @returns {Array<{label: string, flag: string, dotColor: string, country: string|null, entities: Array}>}
 */
export function deriveJurisdictionGroups(entities) {
  const byCountry = new Map();
  for (const e of entities) {
    const key = e.country || '';
    if (!byCountry.has(key)) byCountry.set(key, []);
    byCountry.get(key).push(e);
  }

  const groups = [];
  for (const country of GROUP_ORDER) {
    const ents = byCountry.get(country);
    if (ents?.length) {
      const meta = GROUP_DISPLAY_META[country];
      groups.push({ ...meta, country, groupKey: meta.groupKey, entities: ents });
      byCountry.delete(country);
    }
  }

  // Fallback: any entities whose country is null, empty, or unrecognised
  const remaining = [...byCountry.values()].flat();
  if (remaining.length > 0) {
    groups.push({
      label: 'Other',
      flag: '\uD83D\uDCCB',
      dotColor: 'bg-slate-500',
      groupKey: 'group:other',
      country: null,
      entities: remaining,
    });
  }

  return groups;
}
